import {
  Check,
  MessageSquareText,
  RefreshCw,
  RotateCcw,
  Search,
} from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { api } from "../api";
import {
  ActionButton,
  EmptyState,
  ErrorState,
  LoadingState,
  Pagination,
  Panel,
  SectionHeader,
  Tag,
} from "../components/design-system";
import { FilterPopover } from "../components/filter-popover";
import { useResource } from "../hooks/use-resource";
import type { FeedbackType, UserFeedback } from "../types";
import { formatDateTime } from "../utils";

type StatusFilter = "all" | "pending" | "resolved";
type TypeFilter = "all" | FeedbackType;

const PAGE_SIZE = 12;
const STATUS_FILTERS: Array<{ key: StatusFilter; label: string }> = [
  { key: "pending", label: "待处理" },
  { key: "resolved", label: "已处理" },
  { key: "all", label: "全部" },
];
const TYPE_LABELS: Record<FeedbackType, string> = {
  bug: "问题反馈",
  feature: "功能建议",
  experience: "使用体验",
  other: "其他",
};
const TYPE_FILTERS: Array<{ value: TypeFilter; label: string }> = [
  { value: "all", label: "全部" },
  { value: "bug", label: TYPE_LABELS.bug },
  { value: "feature", label: TYPE_LABELS.feature },
  { value: "experience", label: TYPE_LABELS.experience },
  { value: "other", label: TYPE_LABELS.other },
];
const EMPTY_FEEDBACK_FACETS = {
  all: 0,
  pending: 0,
  resolved: 0,
  types: { all: 0, bug: 0, feature: 0, experience: 0, other: 0 },
};

export function FeedbackPage() {
  const [status, setStatus] = useState<StatusFilter>("pending");
  const [type, setType] = useState<TypeFilter>("all");
  const [page, setPage] = useState(1);
  const [query, setQuery] = useState("");
  const [draftQuery, setDraftQuery] = useState("");
  const [busyId, setBusyId] = useState<string | null>(null);
  const feedback = useResource(
    () =>
      api.feedback({
        page,
        pageSize: PAGE_SIZE,
        q: query,
        status: status === "all" ? undefined : status,
        type: type === "all" ? undefined : type,
      }),
    [page, query, status, type],
  );
  const facets = feedback.data?.facets || EMPTY_FEEDBACK_FACETS;
  const typeFilters = TYPE_FILTERS.map((filter) => ({
    ...filter,
    count: facets.types[filter.value],
  }));

  const setResolved = async (item: UserFeedback) => {
    if (busyId) return;
    setBusyId(item.id);
    try {
      await api.setFeedbackResolved(item.id, !item.resolved);
      toast.success(item.resolved ? "反馈已重新打开" : "反馈已标记为已处理");
      await feedback.refresh();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "反馈状态更新失败");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-6">
      <SectionHeader
        eyebrow="VOICE"
        title="用户反馈"
        desc={
          feedback.data
            ? `共 ${feedback.data.pagination.total} 条匹配反馈`
            : "查看用户提交的问题、建议与体验反馈"
        }
        className="rise-in"
        action={
          <ActionButton
            busy={feedback.refreshing}
            onClick={() => void feedback.refresh()}
          >
            <RefreshCw size={13} />
            刷新
          </ActionButton>
        }
      />

      <div className="rise-in rise-in-1 relative z-20 flex flex-wrap items-center gap-2">
        {STATUS_FILTERS.map((filter) => (
          <button
            key={filter.key}
            onClick={() => {
              setStatus(filter.key);
              setPage(1);
            }}
            className={`rounded-full border px-4 py-1.5 text-[12.5px] font-medium transition active:scale-95 ${status === filter.key ? "border-ink bg-ink text-paper" : "border-sand-light bg-paper text-ink-soft hover:border-sand"}`}
          >
            {filter.label}
            <span className="ml-1.5 text-[10.5px] opacity-60">
              {facets[filter.key]}
            </span>
          </button>
        ))}
        <FilterPopover
          label="类型"
          value={type}
          options={typeFilters}
          onChange={(nextType) => {
            setType(nextType);
            setPage(1);
          }}
          className="ml-auto"
        />
        <form
          className="flex min-w-52 items-center gap-2 rounded-full border border-sand-light bg-paper px-4 py-1.5 text-[12.5px]"
          onSubmit={(event) => {
            event.preventDefault();
            setPage(1);
            setQuery(draftQuery.trim());
          }}
        >
          <Search size={13} className="text-ink-faint" />
          <input
            value={draftQuery}
            onChange={(event) => setDraftQuery(event.target.value)}
            placeholder="搜索用户或内容…"
            aria-label="搜索用户或反馈内容"
            className="w-44 bg-transparent outline-none placeholder:text-ink-faint/70"
          />
        </form>
      </div>

      {feedback.error ? (
        <ErrorState
          error={feedback.error}
          onRetry={() => void feedback.refresh()}
        />
      ) : null}
      <Panel className="rise-in rise-in-2 overflow-hidden">
        {feedback.loading && !feedback.data ? (
          <LoadingState rows={8} />
        ) : feedback.data?.items.length ? (
          <>
            <ol className="divide-y divide-sand-light/70">
              {feedback.data.items.map((item) => (
                <FeedbackItem
                  key={item.id}
                  item={item}
                  busy={busyId === item.id}
                  onToggle={() => void setResolved(item)}
                />
              ))}
            </ol>
            <Pagination
              page={feedback.data.pagination.page}
              pages={feedback.data.pagination.totalPages}
              total={feedback.data.pagination.total}
              onChange={setPage}
              className="border-t border-sand-light/70 px-5 py-4"
            />
          </>
        ) : (
          <EmptyState
            title="没有匹配的反馈"
            description="调整状态、类型或搜索条件后再试。"
          />
        )}
      </Panel>
    </div>
  );
}

function FeedbackItem({
  item,
  busy,
  onToggle,
}: {
  item: UserFeedback;
  busy: boolean;
  onToggle: () => void;
}) {
  return (
    <li className="group grid gap-4 px-5 py-5 transition hover:bg-cream/45 md:grid-cols-[minmax(0,1fr)_auto] md:items-center md:px-6">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <Tag tone={item.type === "bug" ? "clay" : "neutral"}>
            {TYPE_LABELS[item.type]}
          </Tag>
          <Tag tone={item.resolved ? "sage" : "danger"}>
            {item.resolved ? "已处理" : "待处理"}
          </Tag>
          <span className="font-mono text-[10.5px] text-ink-faint">
            #{item.id}
          </span>
          <span className="ml-auto text-[11px] text-ink-faint">
            {formatDateTime(item.createdAt)}
          </span>
        </div>
        <p className="mt-3 whitespace-pre-wrap break-words text-[13.5px] leading-6 text-ink">
          {item.content}
        </p>
        <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11.5px] text-ink-faint">
          <span className="inline-flex items-center gap-1.5">
            <MessageSquareText size={12} />
            {item.displayName} · {item.studentNumber || "—"}
          </span>
          {item.resolved && item.resolvedBy ? (
            <span>
              {item.resolvedBy} · {formatDateTime(item.resolvedAt)}
            </span>
          ) : null}
        </div>
      </div>
      <ActionButton
        tone={item.resolved ? "light" : "dark"}
        busy={busy}
        onClick={onToggle}
        aria-label={
          item.resolved ? "将反馈重新标记为待处理" : "将反馈标记为已处理"
        }
      >
        {item.resolved ? <RotateCcw size={13} /> : <Check size={13} />}
        {item.resolved ? "重新打开" : "标记已处理"}
      </ActionButton>
    </li>
  );
}
